export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export const apiJson = async <T>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });

  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof body === "object" && body && "error" in body
        ? String(body.error)
        : `Request failed with ${response.status}`;
    throw new ApiError(message, response.status);
  }

  return body as T;
};

export type ChatUsage = {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
};

export type ApiStreamEvent =
  | { type: "delta"; content: string }
  | { type: "done"; id: string; model: string; usage?: ChatUsage; mocked: boolean }
  | { type: "error"; message: string };

export const apiStream = async (
  path: string,
  init: RequestInit,
  onEvent: (event: ApiStreamEvent) => void
): Promise<void> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });

  if (!response.ok || !response.body) {
    let message = `Request failed with ${response.status}`;
    try {
      const body = await response.json();
      if (typeof body === "object" && body && "error" in body) {
        message = String(body.error);
      }
    } catch {
      // Keep the status-based message.
    }
    throw new ApiError(message, response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      onEvent(JSON.parse(trimmed.slice(5).trim()) as ApiStreamEvent);
    }
  }
};
